"use client";

import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface PaddingHandleProps {
    side: 'top' | 'bottom';
    currentPadding: number;
    onPaddingChange: (newPadding: number) => void;
}

export function PaddingHandle({ side, currentPadding, onPaddingChange }: PaddingHandleProps) {
    const [isResizing, setIsResizing] = useState(false);
    const [displayPadding, setDisplayPadding] = useState(currentPadding);
    const startY = useRef(0);
    const startPadding = useRef(0);

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        startY.current = e.clientY;
        startPadding.current = currentPadding;
        setDisplayPadding(currentPadding);
    };

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e: MouseEvent) => {
            const deltaY = e.clientY - startY.current;
            let paddingChange = 0;

            if (side === 'top') {
                // Dragging down (positive delta) increases top padding
                paddingChange = deltaY;
            } else {
                // Dragging down (positive delta) decreases bottom padding (moves bottom edge down, away from content? No.)
                // Usually for bottom handle:
                // Dragging DOWN = Increasing padding (gap between content and bottom border) ??
                // Wait.
                // Padding is space INSIDE the boundary.
                // Text --- 
                // [ Padding Bottom ]
                // --- Bottom Border ---

                // If I drag the Bottom Handle DOWN (away from content), I am INCREASING the padding area.
                // So positive deltaY should INCREASE padding.

                // Oops, wait.
                // Top Handle: Positioned at Top. Content is below it.
                // If I drag Top Handle DOWN (towards content), I am reducing the space? Or pushing content down?
                // Visual Convention:
                // 1. Handle is at the edge of the content-box? Or at the border-box edge?
                // Usually "Visual Padding" handles are inside the element.
                // Or handles are at the border, and dragging OUTWARDS increases size/padding?

                // Let's assume standard behavior:
                // Dragging Top Handle DOWN = Increases Top Padding (pushes content down)?
                // Or Dragging Top Handle UP (away from center) = Increases Top Padding?

                // Let's implement: Dragging AWAY from center increases padding.
                // Top Handle: Drag UP (negative delta) = Increase Padding? 
                // OR Top Handle is fixed at the border. Dragging it changes nothing unless the border moves?
                // Visual Editors usually show a bar representing the padding area itself.

                // Simpler approach:
                // Handle is at the Top Edge of the element.
                // Dragging it DOWN (into the element) = INCREASES TOP PADDING (pushes content further down).
                // Dragging it UP (away from element) = DECREASES TOP PADDING (pulls content up)?
                // No, that feels counter-intuitive if the element size is growing.

                // Let's try: Dragging Top Handle DOWN (towards content) = increases padding (more space).
                // Dragging Bottom Handle UP (towards content) = increases padding (more space).

                // Wait, if I drag Top Handle DOWN, I am physically moving the handle down. The handle usually sits at the top edge of the padding area (which is the element border).
                // Actually, maybe the handle should sit on the Content Edge?

                // Let's stick to the User Request: "Dragging the 'Top' handle down increases padding-top."
                paddingChange = deltaY;
            }

            // For Bottom: "Dragging the 'Bottom' handle up increases padding-bottom."
            if (side === 'bottom') {
                // Dragging UP is negative Y.
                // So -deltaY increases padding.
                paddingChange = -deltaY;
            }

            const newPadding = Math.max(0, startPadding.current + paddingChange);

            setDisplayPadding(newPadding);
            onPaddingChange(newPadding);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, onPaddingChange, side]);

    // Sync display padding
    useEffect(() => {
        if (!isResizing) {
            setDisplayPadding(currentPadding);
        }
    }, [currentPadding, isResizing]);

    return (
        <div
            className={cn(
                "absolute left-1/2 -translate-x-1/2 z-50 group/padding flex flex-col items-center justify-center cursor-row-resize h-6 w-32",
                side === 'top' ? "top-0" : "bottom-0"
            )}
            onMouseDown={handleMouseDown}
            style={{ touchAction: 'none' }}
        >
            {/* Visual Bar */}
            <div className={cn(
                "w-full h-1 rounded-full transition-all duration-200",
                isResizing ? "bg-green-500 scale-y-150" : "bg-transparent group-hover/padding:bg-green-300/80"
            )} />

            {/* Tooltip Value */}
            {isResizing && (
                <div className={cn(
                    "absolute bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap pointer-events-none",
                    side === 'top' ? "top-6" : "bottom-6"
                )}>
                    {Math.round(displayPadding)}px
                </div>
            )}
        </div>
    );
}
